/**
 * ThemeSelectionModal - Modal for selecting theme preference on first login
 * Uses shadcn/ui Dialog component
 */
import { useState, useEffect, memo } from 'react';
import Modal from './Modal';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Sun, Moon, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ThemeSelectionModalProps {
  isOpen: boolean;
  onSelect: (theme: string) => void;
  currentTheme?: string;
}

const ThemeSelectionModal = memo(({ isOpen, onSelect, currentTheme = 'dark' }: ThemeSelectionModalProps) => {
  const [selectedTheme, setSelectedTheme] = useState(currentTheme);

  useEffect(() => {
    setSelectedTheme(currentTheme);
  }, [currentTheme]);

  const handleConfirm = () => {
    onSelect(selectedTheme);
  };

  const themes = [
    {
      value: 'light',
      label: 'Light',
      icon: Sun,
      description: 'Clean and bright interface',
    },
    {
      value: 'dark',
      label: 'Dark',
      icon: Moon,
      description: 'Easy on the eyes',
    },
    {
      value: 'system',
      label: 'System',
      icon: Monitor,
      description: 'Follow your device settings',
    },
  ];

  return (
    // Escape confirms the current selection (no keyboard trap); outside-click is
    // blocked (dismissible={false}) so the first-run choice isn't dismissed by an
    // accidental misclick. onClose runs on Escape / X / Continue.
    <Modal
      isOpen={isOpen}
      onClose={handleConfirm}
      dismissible={false}
      size="sm"
      title={<span className="text-2xl font-bold">Welcome to Hestia</span>}
      description="Let's personalize your experience. Choose your preferred theme to get started."
    >
        <div className="py-2">
          <RadioGroup value={selectedTheme} onValueChange={setSelectedTheme} className="space-y-3">
            {themes.map((theme) => {
              const Icon = theme.icon;
              const isSelected = selectedTheme === theme.value;
              
              return (
                <div key={theme.value}>
                  <Label
                    htmlFor={theme.value}
                    className={cn(
                      "flex items-center space-x-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
                      "hover:shadow-md",
                      isSelected
                        ? "border-primary bg-primary/10 shadow-sm"
                        : "border-border hover:border-primary/50 hover:bg-accent/50"
                    )}
                  >
                    <RadioGroupItem value={theme.value} id={theme.value} className="mt-0" />
                    <div className="flex items-center space-x-3 flex-1">
                      <div className={cn(
                        "p-2 rounded-md transition-colors",
                        isSelected ? "bg-primary/20" : "bg-muted"
                      )}>
                        <Icon className={cn(
                          "h-5 w-5",
                          isSelected ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      <div className="flex-1">
                        <div className={cn(
                          "font-semibold text-base",
                          isSelected ? "text-foreground" : "text-muted-foreground"
                        )}>
                          {theme.label}
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5">
                          {theme.description}
                        </div>
                      </div>
                    </div>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={handleConfirm}
            className="min-w-[120px]"
          >
            Continue
          </Button>
        </div>
    </Modal>
  );
});

ThemeSelectionModal.displayName = 'ThemeSelectionModal';

export default ThemeSelectionModal;
